<div class="terms">
  <nav class="legal-nav">
    <a [routerLink]="'/terms-of-use'" class="active">Terms of Use</a>
    <a [routerLink]="'/privacy-policy'">Privacy Policy</a>
    <a [routerLink]="'/subscription-terms'">Subscription Terms</a>
  </nav>

  <h1 class="center"><strong>JustTeach Terms of Use</strong></h1>
  <p class="center last-updated">Last Updated: December 2025</p>

  <p class="intro">
    Welcome to JustTeach! Please read these Terms of Use ("Terms") carefully, as they govern your
    use of the JustTeach AI platform and services (the "Service" or "JustTeach").
  </p>
  <p class="intro">
    These Terms constitute a legally binding agreement between you, as an educator or school
    administrator ("User," "you," or "your"), and Justspeak ltd ("Company," "we," "us," or
    "our"), the operator of the JustTeach platform.
  </p>
  <p class="intro">
    By accessing or using the Service, you signify that you have read, understood, and agree to be
    bound by these Terms and the JustTeach
    <a [routerLink]="'/privacy-policy'">Privacy Policy</a>. If you do not agree to these Terms, you
    may not use the Service.
  </p>

  <section>
    <h2>1. The Service</h2>
    <p>
      JustTeach is a free AI-powered platform designed for educators to automate and streamline
      time-consuming tasks, including lesson planning, assessment creation, and grading. The Service
      is accessible via our website, currently located at
      <a href="https://justteach.ai">justteach.ai</a>.
    </p>
    <p>The core features of the Service include:</p>
    <ul>
      <li>AI-powered lesson plan generation based on user input (subject, pacing guides).</li>
      <li>Automatic creation and grading of assessments.</li>
      <li>
        Real-time tracking of student progress (where applicable and in compliance with Section 4).
      </li>
    </ul>
  </section>

  <section>
    <h2>2. Eligibility and Account Registration</h2>
    <h3>2.1. Eligibility</h3>
    <p>
      The Service is intended for use by professional educators and school administrators. By using
      the Service, you represent and warrant that you are at least 18 years of age and are
      authorized to act on behalf of your school or district, if applicable.
    </p>
    <h3>2.2. Account Security</h3>
    <p>
      When you register for an account, you agree to provide accurate and complete information. You
      are solely responsible for maintaining the confidentiality of your account password and for
      any activities that occur under your account. You must notify us immediately of any
      unauthorized use of your account.
    </p>
  </section>

  <section>
    <h2>3. Subscriptions and Fees</h2>
    <p>
      JustTeach offers a comprehensive free tier with core features. We reserve the right to
      introduce premium features or paid subscription models in the future. Any such changes will be
      governed by additional terms presented to you at the time of purchase. Your access to the
      current free tier is not guaranteed to remain free indefinitely.
    </p>
  </section>

  <section>
    <h2>4. Compliance with Education Laws (FERPA & COPPA)</h2>
    <h3>4.1. Student Data Security</h3>
    <p>
      We are committed to protecting the privacy of student data. The Company designs and operates
      the Service to comply with all applicable U.S. federal and state student data privacy laws,
      including the Family Educational Rights and Privacy Act (FERPA) and the Children's Online
      Privacy Protection Act (COPPA).
    </p>
    <h3>4.2. Your Responsibility</h3>
    <p>
      When using features that involve student data (such as assessment grading or progress
      tracking), you agree to comply with all applicable privacy laws, including obtaining any
      necessary parental consent required by your school, district, and relevant laws, before
      inputting or processing any Personally Identifiable Information (PII) of students through the
      Service.
    </p>
  </section>

  <section>
    <h2>5. User Content and Intellectual Property</h2>
    <h3>5.1. JustTeach Content</h3>
    <p>
      All content, technology, software, and intellectual property (including, but not limited to,
      algorithms, logos, trademarks, and design) related to the operation of the Service are owned
      by Justspeak ltd and are protected by applicable intellectual property laws.
    </p>
    <h3>5.2. User Content Ownership</h3>
    <p>
      You retain all ownership rights to the lesson plans, assessment questions, student lists, and
      any other data or educational materials you create, upload, input, or generate using the
      Service ("User Content").
    </p>
    <h3>5.3. License to JustTeach</h3>
    <p>
      By submitting User Content, you grant JustTeach a non-exclusive, worldwide, royalty-free
      license to use, reproduce, modify, and display the User Content solely for the purpose of
      operating, improving, and providing the Service to you and other users. This license
      terminates when you delete your User Content or your account, unless required by law or
      retained in anonymized form for product improvement.
    </p>
    <h3>5.4. AI-Generated Content</h3>
    <p>
      The lesson plans and assessments generated by the AI are based on your inputs and our
      proprietary models. While you retain ownership of your specific inputs, the underlying AI
      models and processes remain the exclusive property of JustTeach.
    </p>
  </section>

  <section>
    <h2>6. Acceptable Use</h2>
    <p>You agree not to use the Service:</p>
    <ul>
      <li>
        For any illegal purpose or in violation of any local, state, national, or international law,
        including laws governing intellectual property and student privacy.
      </li>
      <li>
        To generate, upload, or transmit any content that is harmful, harassing, defamatory, or
        violates the rights of any third party.
      </li>
      <li>To interfere with or disrupt the integrity or performance of the Service.</li>
      <li>To attempt to gain unauthorized access to the Service or its related systems.</li>
    </ul>
  </section>

  <section>
    <h2>7. Termination</h2>
    <p>
      We may terminate or suspend your account and access to the Service immediately, without prior
      notice or liability, if you breach these Terms. Upon termination, your right to use the
      Service will immediately cease. If you wish to terminate your account, you may do so by
      following the provided instructions within the platform or contacting our support team.
    </p>
  </section>

  <section>
    <h2>8. Disclaimer of Warranties</h2>
    <p class="disclaimer">
      The Service is provided on an "as is" and "as available" basis. Justspeak ltd makes no
      warranties, expressed or implied, including, but not limited to, implied warranties of
      merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that
      the Service will be uninterrupted, secure, or error-free, or that the AI-generated content
      will be free of inaccuracies or suitable for any specific educational use.
    </p>
  </section>

  <section>
    <h2>9. Limitation of Liability</h2>
    <p class="disclaimer">
      In no event shall Justspeak ltd, its directors, employees, or agents be liable for any
      indirect, punitive, incidental, special, consequential, or exemplary damages, including
      without limitation damages for loss of profits, goodwill, use, data, or other intangible
      losses, arising out of or relating to your use of or inability to use the Service.
    </p>
  </section>

  <section>
    <h2>10. Governing Law and Dispute Resolution</h2>
    <p>
      These Terms shall be governed by the laws of Cyprus, without regard to its conflict of law
      principles. Any legal action or proceeding arising under these Terms shall be brought
      exclusively in the courts located in Cyprus.
    </p>
  </section>

  <section>
    <h2>11. Contact Information</h2>
    <p>If you have any questions about these Terms, please contact us at:</p>
    <div class="contact-info">
      <p><strong>Justspeak ltd</strong></p>
      <p>
        Email:
        <a href="mailto:justteachsupport@justteach.ai">justteachsupport&#64;justteach.ai</a>
      </p>
      <p>Website: <a href="https://justteach.ai">justteach.ai</a></p>
    </div>
  </section>
</div>

